/**
 * Teams API Hook
 * 
 * Provides hooks for managing teams with demo/live mode support
 * Handles API integration when backend is available
 */

import { useState, useCallback } from 'react';
import { demoTeams, demoPermissions } from '@/data/demo-teams';
import type { Team, TeamCreateDto, TeamFilters, TeamMember, Permission } from '@/types/teams';
import { getApiUrl } from '@/lib/api-config';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getModuleMode } from '@/lib/feature-mode';

/**
 * Get the API base URL using the same logic as DataService.
 * Checks configured URL, env variable, and backend URL detection.
 */
const getApiBaseUrl = (): string => {
  const apiUrlFromConfig = getApiUrl();
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  const backendUrl = getBackendUrl();
  const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

  // In E2B environment, always use the detected backend URL
  // Otherwise, use the configured URL or env URL as fallback
  const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);
  return baseUrl.replace(/\/$/, '');
};

/**
 * Build headers for API requests including auth token and instance ID.
 */
const buildHeaders = async (additionalHeaders?: Record<string, string>): Promise<Record<string, string>> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  const authConfig = getAuthConfig();
  if (authConfig.getAccessToken) {
    try {
      const token = await authConfig.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting access token:', error);
    }
  }

  headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
  return headers;
};

export function useTeams() {
  // Read the feature mode directly from localStorage to avoid timing issues
  // with the async mode sync in ModeProvider. This ensures we always use
  // the correct mode based on the feature settings for Team Settings.
  const getIsDemo = useCallback(() => {
    return getModuleMode('teamSettings') === 'demo';
  }, []);
  
  const [teams, setTeams] = useState<Team[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Load teams based on mode
  const loadTeams = useCallback(async (filters?: TeamFilters) => {
    setLoading(true);
    setError(null);
    const isDemo = getIsDemo();

    try {
      if (isDemo) {
        // Demo mode - use local data
        await new Promise(resolve => setTimeout(resolve, 300));
        let filteredTeams = [...demoTeams];

        // Apply filters
        if (filters?.search) {
          const search = filters.search.toLowerCase();
          filteredTeams = filteredTeams.filter(
            team =>
              team.name.toLowerCase().includes(search) ||
              team.description?.toLowerCase().includes(search)
          );
        }

        setTeams(filteredTeams);
      } else {
        // Live mode - fetch from API
        const queryParams = new URLSearchParams();
        if (filters?.search) queryParams.append('search', filters.search);

        const headers = await buildHeaders();
        const response = await fetch(
          `${getApiBaseUrl()}/api/teams?${queryParams.toString()}`,
          { headers }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch teams from API');
        }

        const data = await response.json();
        setTeams(data);
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      // Only fallback to demo data if in demo mode
      // In live mode, keep teams empty to avoid mixing demo IDs with live API calls
      if (isDemo) {
        setTeams(demoTeams);
      } else {
        setTeams([]);
      }
    } finally {
      setLoading(false);
    }
  }, [getIsDemo]);

  // Load permission definitions from backend
  const loadPermissionDefinitions = useCallback(async () => {
    setPermissionsLoading(true);
    const isDemo = getIsDemo();

    try {
      if (isDemo) {
        // Demo mode - use local data
        await new Promise(resolve => setTimeout(resolve, 200));
        setPermissions(demoPermissions);
      } else {
        // Live mode - fetch from API
        const headers = await buildHeaders();
        const response = await fetch(
          `${getApiBaseUrl()}/api/teams/permissions`,
          { headers }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch permission definitions from API');
        }

        const data = await response.json();
        setPermissions(data);
      }
    } catch (err) {
      console.error('Error loading permission definitions:', err);
      // Fallback to demo permissions in case of error
      if (isDemo) {
        setPermissions(demoPermissions);
      } else {
        // In live mode, keep permissions empty to avoid mixing demo IDs with live API calls
        setPermissions([]);
      }
    } finally {
      setPermissionsLoading(false);
    }
  }, [getIsDemo]);

  // Create team
  const createTeam = useCallback(
    async (data: TeamCreateDto): Promise<Team> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - simulate creation
        await new Promise(resolve => setTimeout(resolve, 800));

        const newTeam: Team = {
          id: `team_${Date.now()}`,
          ...data,
          isDefault: false,
          canDelete: true,
          canRename: true,
          memberCount: 0,
          permissions: [],
          createdAt: new Date().toISOString(),
          createdBy: 'current_user',
          updatedAt: new Date().toISOString(),
          updatedBy: 'current_user',
        };

        setTeams(prev => [...prev, newTeam]);
        return newTeam;
      } else {
        // Live mode - create via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams`, {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error('Failed to create team');
        }

        const newTeam = await response.json();
        setTeams(prev => [...prev, newTeam]);
        return newTeam;
      }
    },
    [getIsDemo]
  );

  // Update team
  const updateTeam = useCallback(
    async (teamId: string, updates: Partial<Team>): Promise<void> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - simulate update
        await new Promise(resolve => setTimeout(resolve, 500));

        setTeams(prev =>
          prev.map(team =>
            team.id === teamId
              ? {
                  ...team,
                  ...updates,
                  updatedAt: new Date().toISOString(),
                  updatedBy: 'current_user',
                }
              : team
          )
        );
      } else {
        // Live mode - update via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(updates),
        });

        if (!response.ok) {
          throw new Error('Failed to update team');
        }

        const updatedTeam = await response.json();
        setTeams(prev =>
          prev.map(team => (team.id === teamId ? updatedTeam : team))
        );
      }
    },
    [getIsDemo]
  );

  // Delete team
  const deleteTeam = useCallback(
    async (teamId: string): Promise<void> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - simulate deletion
        await new Promise(resolve => setTimeout(resolve, 500));
        setTeams(prev => prev.filter(team => team.id !== teamId));
      } else {
        // Live mode - delete via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to delete team');
        }

        setTeams(prev => prev.filter(team => team.id !== teamId));
      }
    },
    [getIsDemo]
  );

  // Delete multiple teams
  const deleteTeams = useCallback(
    async (teamIds: string[]): Promise<void> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - simulate bulk deletion
        await new Promise(resolve => setTimeout(resolve, 500));
        setTeams(prev => prev.filter(team => !teamIds.includes(team.id)));
      } else {
        // Live mode - bulk delete via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/bulk-delete`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ teamIds }),
        });

        if (!response.ok) {
          throw new Error('Failed to delete teams');
        }

        setTeams(prev => prev.filter(team => !teamIds.includes(team.id)));
      }
    },
    [getIsDemo]
  );

  // Get team by ID
  const getTeam = useCallback(
    async (teamId: string): Promise<Team | null> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - find in local data
        return teams.find(team => team.id === teamId) ?? null;
      } else {
        // Live mode - fetch from API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}`, {
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch team');
        }

        return await response.json();
      }
    },
    [getIsDemo, teams]
  );

  // Update team permissions
  const updateTeamPermissions = useCallback(
    async (teamId: string, grantedPermissions: string[]): Promise<void> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - simulate update
        await new Promise(resolve => setTimeout(resolve, 500));
        console.log('Demo mode: Would update permissions for team', teamId, 'with', grantedPermissions);
      } else {
        // Live mode - update via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}/permissions`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ grantedPermissions }),
        });

        if (!response.ok) {
          throw new Error('Failed to update team permissions');
        }

        // Reload teams to get updated data
        await loadTeams();
      }
    },
    [getIsDemo, loadTeams]
  );

  // Add a member to a team
  const addTeamMember = useCallback(
    async (teamId: string, member: Omit<TeamMember, 'assignedAt' | 'assignedBy'>): Promise<TeamMember> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - simulate adding member
        await new Promise(resolve => setTimeout(resolve, 500));
        const newMember: TeamMember = {
          ...member,
          assignedAt: new Date().toISOString(),
          assignedBy: 'current_user',
        };
        // Update local team member count
        setTeams(prev =>
          prev.map(team =>
            team.id === teamId
              ? { ...team, memberCount: team.memberCount + 1 }
              : team
          )
        );
        return newMember;
      } else {
        // Live mode - add via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}/members`, {
          method: 'POST',
          headers,
          body: JSON.stringify(member),
        });

        if (!response.ok) {
          throw new Error('Failed to add team member');
        }

        const addedMember = await response.json();
        // Reload teams to get updated member count
        await loadTeams();
        return addedMember;
      }
    },
    [getIsDemo, loadTeams]
  );

  // Add multiple members to a team
  const addTeamMembers = useCallback(
    async (teamId: string, members: Array<Omit<TeamMember, 'assignedAt' | 'assignedBy'>>): Promise<TeamMember[]> => {
      const addedMembers: TeamMember[] = [];
      for (const member of members) {
        const added = await addTeamMember(teamId, member);
        addedMembers.push(added);
      }
      return addedMembers;
    },
    [addTeamMember]
  );

  // Remove a member from a team
  const removeTeamMember = useCallback(
    async (teamId: string, userId: string): Promise<boolean> => {
      const isDemo = getIsDemo();
      if (isDemo) {
        // Demo mode - simulate removal
        await new Promise(resolve => setTimeout(resolve, 500));
        // Update local team member count
        setTeams(prev =>
          prev.map(team =>
            team.id === teamId
              ? { ...team, memberCount: Math.max(0, team.memberCount - 1) }
              : team
          )
        );
        return true;
      } else {
        // Live mode - remove via API
        const headers = await buildHeaders();
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${teamId}/members/${userId}`, {
          method: 'DELETE',
          headers,
        });

        if (!response.ok) {
          throw new Error('Failed to remove team member');
        }

        // Reload teams to get updated member count
        await loadTeams();
        return true;
      }
    },
    [getIsDemo, loadTeams]
  );

  // Remove multiple members from a team
  const removeTeamMembers = useCallback(
    async (teamId: string, userIds: string[]): Promise<boolean> => {
      for (const userId of userIds) {
        await removeTeamMember(teamId, userId);
      }
      return true;
    },
    [removeTeamMember]
  );

  return {
    teams,
    permissions,
    loading,
    permissionsLoading,
    error,
    loadTeams,
    loadPermissionDefinitions,
    createTeam,
    updateTeam,
    deleteTeam,
    deleteTeams,
    getTeam,
    updateTeamPermissions,
    addTeamMember,
    addTeamMembers,
    removeTeamMember,
    removeTeamMembers,
  };
}
